/**
 * Utility class for creating and manipulating `IVector3` objects.
 */
export class IVector3 {
    constructor(...args) {
        if (args.length === 0) {
            return { x: 0, y: 0, z: 0 };
        }
        else if (args.length === 1) {
            const [value] = args;
            return { x: value, y: value, z: value };
        }
        else if (args.length === 2) {
            const [xy, z] = args;
            return Object.assign(Object.assign({}, xy), { z: z });
        }
        else {
            const [x, y, z] = args;
            return { x: x, y: y, z: z };
        }
    }
    /**
     * Returns a new `IVector3` object with all components set to 0.
     * @returns Anonymous object `{ x: 0, y: 0, z: 0 }`
     */
    static Zero() {
        return new IVector3();
    }
    /**
     * Returns a new `IVector3` object with all components set to 1.
     * @returns Anonymous object `{ x: 1, y: 1, z: 1 }`
     */
    static One() {
        return new IVector3(1);
    }
    /**
     * Returns a new `IVector3` object that points up.
     * @returns Anonymous object `{ x: 0, y: 1, z: 0 }`
     */
    static Up() {
        return new IVector3(0, 1, 0);
    }
    /**
     * Returns a new `IVector3` object that points down.
     * @returns Anonymous object `{ x: 0, y: -1, z: 0 }`
     */
    static Down() {
        return new IVector3(0, -1, 0);
    }
    /**
     * Returns a new `IVector3` object that points right.
     * @returns Anonymous object `{ x: 1, y: 0, z: 0 }`
     */
    static Right() {
        return new IVector3(1, 0, 0);
    }
    /**
     * Returns a new `IVector3` object that points left.
     * @returns anonymous object `{ x: -1, y: 0, z: 0 }`
     */
    static Left() {
        return new IVector3(-1, 0, 0);
    }
    /**
     * Returns a new `IVector3` object that points forward.
     * @returns Anonymous object `{ x: 0, y: 0, z: 1 }`
     */
    static Forward() {
        return new IVector3(0, 0, 1);
    }
    /**
     * @deprecated replaced with Backward().
     * Returns a new `IVector3` object that points backward.
     * @returns Anonymous object `{ x: 0, y: 0, z: -1 }`
     */
    static Back() {
        return new IVector3(0, 0, -1);
    }
    /**
     * Returns a new `IVector3` object that points backward.
     * @returns Anonymous object `{ x: 0, y: 0, z: -1 }`
     */
    static Backward() {
        return new IVector3(0, 0, -1);
    }
    /**
     * Type guard to check if the provided object is applicable to the `IVector3` interface.
     * @returns Type assertion if the object is applicable to the IVector3.
     */
    static is(value) {
        const vector = value;
        return 'x' in vector && 'y' in vector && 'z' in vector && 'number' && typeof vector.y === 'number' && typeof vector.z === 'number';
    }
    /**
     * Creates a new `IVector3` object as a verbose copy of the provided vector.
     * @returns A new `IVector3` object.
     */
    static copy(vector) {
        return new IVector3(vector.x, vector.y, vector.z);
    }
    /**
     * Copies the values from one `IVector3` object to another.
     * @note This method directly modifies the `result` vector.
     */
    static copy_to_ref(vector, result) {
        result.x = vector.x;
        result.y = vector.y;
        result.z = vector.z;
        return result;
    }
    /**
     * Creates an `IVector3` object from an array of numbers starting at the specified offset.
     * @returns A new `IVector3` object.
    */
    static from_array(array, offset = 0) {
        return new IVector3(array[offset], array[offset + 1], array[offset + 2]);
    }
    /**
     * Sets the values to an existing `IVector3` object from an array of numbers starting at the specified offset.
     * @note This method directly modifies the `result` vector.
     */
    static from_array_to_ref(array, offset, result) {
        result.x = array[offset];
        result.y = array[offset + 1];
        result.z = array[offset + 2];
    }
    /**
     * Directly sets the values to a `IVector3` object.
     * @note This method directly modifies the `target` vector.
     */
    static set(target, x, y, z) {
        target.x = x;
        target.y = y;
        target.z = z;
        return target;
    }
    /**
     * Calculates the magnitude (length) of the given `IVector3`.
     * @returns The magnitude of the vector.
     */
    static magnitude(vector) {
        return Math.sqrt(IVector3.magnitude_squared(vector));
    }
    /**
     * Calculates the squared magnitude of the given `IVector3`.
     * This method is useful for performance-sensitive situations.
     * @returns The squared magnitude of the vector.
     */
    static magnitude_squared(vector) {
        return Math.pow(vector.x, 2) + Math.pow(vector.y, 2) + Math.pow(vector.z, 2);
    }
    /**
     * Normalizes the given `IVector3` object and returns a new object with the normalized values.
     * @returns A new `IVector3` object.
     */
    static normalize(vector) {
        const magnitude = IVector3.magnitude(vector);
        if (magnitude === 0) {
            // TODO: Add a verbose warning.
        }
        return new IVector3(vector.x / magnitude, vector.y / magnitude, vector.z / magnitude);
    }
    /**
     * Normalizes the given `IVector3` if it's not the zero-vector and returns a new object with the normalized values, otherwise returns a new object with zeroes.
     * @returns A new `IVector3` object.
     */
    static normalize_safe(vector) {
        const magnitude = IVector3.magnitude(vector);
        if (magnitude === 0) {
            return new IVector3(0, 0, 0);
        }
        return new IVector3(vector.x / magnitude, vector.y / magnitude, vector.z / magnitude);
    }
    /**
     * Normalizes the given `IVector3` object values and sets them to the specified vector.
     * @note This method directly modifies the `result` vector.
     */
    static normalize_to_ref(vector, result) {
        const magnitude = IVector3.magnitude(vector);
        if (magnitude === 0) {
            // TODO: Add a verbose warning.
        }
        result.x = vector.x / magnitude;
        result.y = vector.y / magnitude;
        result.z = vector.z / magnitude;
    }
    /**
     * Normalizes the given `IVector3` object in place.
     * @note This method directly modifies the `result` vector.
     * @returns The modified `IVector3` object reference.
     */
    static normalize_in_place(vector) {
        const magnitude = IVector3.magnitude(vector);
        if (magnitude === 0) {
            // TODO: Add a verbose warning.
        }
        vector.x /= magnitude;
        vector.y /= magnitude;
        vector.z /= magnitude;
        return vector;
    }
    /**
     * Calculates the distance between two `IVector3` instances.
     * @returns The distance between vector1 and vector2.
     */
    static distance(vector1, vector2) {
        return Math.sqrt(IVector3.distance_squared(vector1, vector2));
    }
    /**
     * Calculates the squared distance between two `IVector3` instances.
     * This method is useful for performance-sensitive situations.
     * @returns The squared distance between vector1 and vector2.
     */
    static distance_squared(vector1, vector2) {
        const dx = vector1.x - vector2.x;
        const dy = vector1.y - vector2.y;
        const dz = vector1.z - vector2.z;
        return Math.pow(dx, 2) + Math.pow(dy, 2) + Math.pow(dz, 2);
    }
    /**
     * Scales an `IVector3` object by a scalar.
     * @returns A new `IVector3` object.
     */
    static scale(vector, scalar) {
        return new IVector3(vector.x * scalar, vector.y * scalar, vector.z * scalar);
    }
    /**
     * Scales an `IVector3` object values by a scalar and sets them to the specified vector.
     * @note This method directly modifies the `result` vector.
     */
    static scale_to_ref(vector, scalar, result) {
        result.x = vector.x * scalar;
        result.y = vector.y * scalar;
        result.z = vector.z * scalar;
        return result;
    }
    /**
     * Scales an IVector3 instance by a scalar in place, modifying its components directly.
     * @note This method directly modifies the `vector` instance.
     * @returns The modified `IVector3` object reference.
     */
    static scale_in_place(vector, scalar) {
        vector.x *= scalar;
        vector.y *= scalar;
        vector.z *= scalar;
        return vector;
    }
    /**
     * Takes a given vector and adds a scaled vector to it as a single operation
     * @param target the vector to work on
     * @param scalar value to scale vector with
     * @param vector vector to scale and add to target
     * @returns target
     */
    static add_scaled_in_place(target, scalar, vector) {
        IVector3.copy_to_ref(vector, IVector3.scale_cache);
        IVector3.scale_in_place(IVector3.scale_cache, scalar);
        IVector3.add_in_place(target, IVector3.scale_cache);
        return target;
    }
    /**
     * scales the second vector with the scalar and adds the result with the first vector
     * @param vector1 vector to add
     * @param scalar scalar for @param vector2
     * @param vector2 vector to scale with @param scalar
     * @returns @param vector1 added with scaled @param vector2
     */
    static add_scaled(vector1, scalar, vector2) {
        const result = IVector3.copy(vector1);
        return IVector3.add_scaled_in_place(result, scalar, vector2);
    }
    /**
     * Checks if two `IVector3` objects are equal by comparing their components.
     * @returns True if the components of vector1 and vector2 are exactly equal.
     */
    static equals(vector1, vector2) {
        return vector1.x === vector2.x && vector1.y === vector2.y && vector1.z === vector2.z;
    }
    /**
     * Checks if two IVector3 instances are approximately equal using an epsilon.
     * This is useful for dealing with floating-point precision issues.
     * @returns True if the differences of the components are less than epsilon.
     */
    static equals_with_epsilon(vector1, vector2, epsilon) {
        return Math.abs(vector1.x - vector2.x) < epsilon &&
            Math.abs(vector1.y - vector2.y) < epsilon &&
            Math.abs(vector1.z - vector2.z) < epsilon;
    }
    /**
     * Adds two `IVector3` objects together and returns a new object with the result.
     * @returns A new `IVector3` objects.
     */
    static add(vector1, vector2) {
        return new IVector3(vector1.x + vector2.x, vector1.y + vector2.y, vector1.z + vector2.z);
    }
    /**
     * Adds two `IVector3` objects together and sets the result to the specified vector.
     * @note This method directly modifies the `result` vector.
     */
    static add_to_ref(vector1, vector2, result) {
        result.x = vector1.x + vector2.x;
        result.y = vector1.y + vector2.y;
        result.z = vector1.z + vector2.z;
        return result;
    }
    /**
     * Adds the components of one `IVector3` object to another in place.
     * @note This method directly modifies the `target` vector.
     * @returns The modified `IVector3` object reference.
     */
    static add_in_place(target, addend) {
        target.x += addend.x;
        target.y += addend.y;
        target.z += addend.z;
        return target;
    }
    /**
     * Sums together the given values
     * @returns A new `IVector3` object
     */
    static sum(...values) {
        const sum = new IVector3(0, 0, 0);
        for (let i = 0; i < values.length; i++) {
            sum.x += values[i].x;
            sum.y += values[i].y;
            sum.z += values[i].z;
        }
        return sum;
    }
    /**
     * Substracts the second `IVector3` object from the first and returns a new object with the result.
     * @returns A new `IVector3` objects.
     */
    static subtract(vector1, vector2) {
        return new IVector3(vector1.x - vector2.x, vector1.y - vector2.y, vector1.z - vector2.z);
    }
    /**
     * Substracts the second `IVector3` object from the first and sets the result to the specified vector.
     * @note This method directly modifies the `result` vector.
     */
    static subtract_to_ref(vector1, vector2, result) {
        result.x = vector1.x - vector2.x;
        result.y = vector1.y - vector2.y;
        result.z = vector1.z - vector2.z;
        return result;
    }
    /**
     * Substracts the second `IVector3` object from the first in place.
     * @note This method directly modifies the `target` vector.
     * @returns The modified `IVector3` object reference.
     */
    static subtract_in_place(target, subtrahend) {
        target.x -= subtrahend.x;
        target.y -= subtrahend.y;
        target.z -= subtrahend.z;
        return target;
    }
    /**
     * Calculates the dot product of two `IVector3` objects.
     * @returns The dot product of two vectors.
     */
    static dot(vector1, vector2) {
        return vector1.x * vector2.x + vector1.y * vector2.y + vector1.z * vector2.z;
    }
    /**
     * Calculates the cross product of two`IVector3` objects.
     * @returns A new `IVector3` object representing the cross product of two vectors.
     */
    static cross(vector1, vector2) {
        return IVector3.cross_to_ref(vector1, vector2, new IVector3());
    }
    static cross_to_ref(vector1, vector2, result) {
        const x = vector1.y * vector2.z - vector1.z * vector2.y;
        const y = vector1.z * vector2.x - vector1.x * vector2.z;
        const z = vector1.x * vector2.y - vector1.y * vector2.x;
        result.x = x;
        result.y = y;
        result.z = z;
        return result;
    }
    /**
     * Multiplies the components of two `IVector3` objects and returns a new object with the result.
     * @returns A new `IVector3` objects.
     */
    static multiply(vector1, vector2) {
        return new IVector3(vector1.x * vector2.x, vector1.y * vector2.y, vector1.z * vector2.z);
    }
    /**
     * Multiplies the components of two `IVector3` objects and sets the values to the specified vector.
     * @note This method directly modifies the `result` vector.
     */
    static multiply_to_ref(vector1, vector2, result) {
        result.x = vector1.x * vector2.x;
        result.y = vector1.y * vector2.y;
        result.z = vector1.z * vector2.z;
        return result;
    }
    /**
     * Multiplies the components of the one `IVector3` object by another in place.
     * @note This method directly modifies the `target` vector.
     * @returns The modified `IVector3` object reference.
     */
    static multiply_in_place(target, multiplier) {
        target.x *= multiplier.x;
        target.y *= multiplier.y;
        target.z *= multiplier.z;
        return target;
    }
    /**
     * Divides the components of the first `IVector3` object by the components of the second.
     * @returns A new `IVector3` objects.
     */
    static divide(vector1, vector2) {
        return new IVector3(vector1.x / vector2.x, vector1.y / vector2.y, vector1.z / vector2.z);
    }
    /**
     * Divides the components of the first `IVector3` object by the components of the second.
     * @note This method directly modifies the `result` vector.
     */
    static divide_to_ref(vector1, vector2, result) {
        result.x = vector1.x / vector2.x;
        result.y = vector1.y / vector2.y;
        result.z = vector1.z / vector2.z;
        return result;
    }
    /**
     * Divides the components of the first `IVector3` object by the components of the second in place.
     * @note This method directly modifies the `target` vector.
     * @returns The modified `IVector3` object reference.
     */
    static divide_in_place(target, divisor) {
        target.x /= divisor.x;
        target.y /= divisor.y;
        target.z /= divisor.z;
        return target;
    }
    /**
     * Returns a new `IVector3` object representing the component-wise minimum of two vectors.
     * @returns A new `IVector3` object.
     */
    static min(vector1, vector2) {
        return new IVector3(Math.min(vector1.x, vector2.x), Math.min(vector1.y, vector2.y), Math.min(vector1.z, vector2.z));
    }
    /**
     * Returns a new `IVector3` object representing the component-wise naximum of two vectors.
     * @returns A new `IVector3` object.
     */
    static max(vector1, vector2) {
        return new IVector3(Math.max(vector1.x, vector2.x), Math.max(vector1.y, vector2.y), Math.max(vector1.z, vector2.z));
    }
    /**
     * Sets the component-wise minimum of two `IVector3` objects to the specified vector.
     * @note This method directly modifies the `result` vector.
     */
    static min_to_ref(vector1, vector2, result) {
        result.x = Math.min(vector1.x, vector2.x);
        result.y = Math.min(vector1.y, vector2.y);
        result.z = Math.min(vector1.z, vector2.z);
        return result;
    }
    /**
     * Sets the component-wise maximum of two `IVector3` objects to the specified vector.
     * @note This method directly modifies the `result` vector.
     */
    static max_to_ref(vector1, vector2, result) {
        result.x = Math.max(vector1.x, vector2.x);
        result.y = Math.max(vector1.y, vector2.y);
        result.z = Math.max(vector1.z, vector2.z);
        return result;
    }
    static lerp(a, b, t) {
        if (typeof t === 'number') {
            const inverse_amount = 1.0 - t;
            return new IVector3(a.x * inverse_amount + b.x * t, a.y * inverse_amount + b.y * t, a.z * inverse_amount + b.z * t);
        }
        else {
            return new IVector3(a.x * (1.0 - t.x) + b.x * t.x, a.y * (1.0 - t.y) + b.y * t.y, a.z * (1.0 - t.z) + b.z * t.z);
        }
    }
    static lerp_to_ref(a, b, t, result) {
        if (typeof t === 'number') {
            const inverse_amount = 1.0 - t;
            result.x = a.x * inverse_amount + b.x * t;
            result.y = a.y * inverse_amount + b.y * t;
            result.z = a.z * inverse_amount + b.z * t;
        }
        else {
            result.x = a.x * (1.0 - t.x) + b.x * t.x;
            result.y = a.y * (1.0 - t.y) + b.y * t.y;
            result.z = a.z * (1.0 - t.z) + b.z * t.z;
        }
        return result;
    }
    /**
     * Calculates the angle in radians between two IVector3 vectors.
     * @returns The angle in radians.
     */
    static angle(vector1, vector2) {
        return Math.acos(IVector3.dot(vector1, vector2) / (IVector3.magnitude(vector1) * IVector3.magnitude(vector2)));
    }
    /**
     * Calculates a vector perpendicular to the given `IVector3` vector.
     * @returns A new `IVector3` object.
     */
    static perpendicular(v) {
        if (Math.abs(v.y) < Math.abs(v.x)) {
            return new IVector3(v.z, 0, -v.x);
        }
        else {
            return new IVector3(0, v.z, -v.y);
        }
    }
    /**
     * Project a `IVector3` onto a plane and return resulting `IVector3`.
     * @returns A new `IVector3` object.
     */
    static project_on_plane(vector, plane_normal) {
        const ortho_component = IVector3.scale(plane_normal, IVector3.dot(vector, plane_normal));
        return IVector3.subtract(vector, ortho_component);
    }
    /**
     * Converts an `IVector3` object to an array of numbers `[ x, y, z ]`.
     * @returns An array `[ number, number, number ]`.
     */
    static to_array(vector3) {
        return [vector3.x, vector3.y, vector3.z];
    }
    /**
     * Converts an `IVector3` object to a `string`.
     * @returns A string in the format `{ x: number, y: number, z: number }`.
     */
    static to_string(vector) {
        return `{ x: ${vector.x}, y: ${vector.y}, z: ${vector.z} }`;
    }
}
IVector3.scale_cache = new IVector3();
//# sourceMappingURL=IVector3.js.map